import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

export interface MetaPixelCompleteRegistrationPayload {
  content_name: string;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class MetaPixelService {
  private readonly renderer: Renderer2;
  private scriptInitialized = false;
  private readonly isEnabled = environment.NAME === 'production';
  private readonly pixelId = environment.META_PIXEL_ID;
  private readonly completeRegistrationStoragePrefix = 'meta_pixel_complete_registration:';

  constructor(
    private readonly rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  init(): void {
    if (!this.isEnabled || this.scriptInitialized) {
      return;
    }

    const inlineScript = this.renderer.createElement('script');
    if (!inlineScript) {
      return;
    }

    inlineScript.text = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${this.pixelId}');
    `;
    this.renderer.appendChild(this.document.head, inlineScript);

    const noscript = this.renderer.createElement('noscript');
    if (noscript) {
      const image = this.renderer.createElement('img');
      if (image) {
        this.renderer.setAttribute(image, 'height', '1');
        this.renderer.setAttribute(image, 'width', '1');
        this.renderer.setAttribute(image, 'style', 'display:none');
        this.renderer.setAttribute(
          image,
          'src',
          `https://www.facebook.com/tr?id=${this.pixelId}&ev=PageView&noscript=1`
        );
        this.renderer.appendChild(noscript, image);
        this.renderer.appendChild(this.document.body, noscript);
      }
    }

    this.scriptInitialized = true;
  }

  trackPageView(): void {
    if (!this.isEnabled) {
      return;
    }

    this.init();
    window.fbq?.('track', 'PageView');
  }

  trackCompleteRegistration(email: string): void {
    if (!this.isEnabled || !email?.trim()) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const storageKey = `${this.completeRegistrationStoragePrefix}${normalizedEmail}`;
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    this.init();
    const payload: MetaPixelCompleteRegistrationPayload = {
      content_name: 'AtomicTax Account Registration',
      status: 'completed'
    };
    window.fbq?.('track', 'CompleteRegistration', payload);
    sessionStorage.setItem(storageKey, '1');
  }
}
